import type { RefObject } from 'react';

import type { CronScheduleBuilderHandle } from '@/components/cron';
import type { CreateTriggerRequest, ScheduleType } from '@/types/schedule';

import type { DailyFormState, RepeatingFormState } from '../types/scheduleFormState';
import type { CronScheduleSaveState } from './cronScheduleSave';
import { resolveEditCronSavePayload } from './cronScheduleSave';
import { buildRepeatingSchedulePayload } from './repeatingSchedulePayload';
import { convertLocalTimeToUTC } from './timezoneData';

export interface ScheduleTriggerBaseIds {
  baseid: number;
  environmentId: number;
}

export interface BuildScheduleTriggerRequestInput {
  mode: 'create' | 'edit';
  scheduleType: ScheduleType;
  daily: DailyFormState;
  repeating: RepeatingFormState;
  cron: CronScheduleSaveState;
  cronBuilderRef: RefObject<CronScheduleBuilderHandle | null>;
  baseIds: ScheduleTriggerBaseIds;
  templateName?: string;
  refDate?: Date;
  /** Stored cronExplain from API — edit passthrough only. */
  storedCronExplain?: string;
  /** Existing repeating rows without stored timeZone (edit only). */
  repeatingLegacyNoTimeZone?: boolean;
  confirmLegacyMigration?: boolean;
  /** Legacy advanced cron missing cronTimeZone — TZ-only save, no byte changes. */
  cronTimezoneBootstrap?: boolean;
  /** Stored schedule opened for TZ and/or TF migration — metadata save when !userEdited. */
  openedForCronMigration?: boolean;
  /** Stored cronTimeZone from API — edit rebuild when author TZ changes. */
  storedCronTimeZone?: string;
  /** Stored cronTimeFrame from API — edit passthrough when cron bytes unchanged. */
  storedCronTimeFrame?: CreateTriggerRequest['cronTimeFrame'];
}

function buildBaseRequest(
  baseIds: ScheduleTriggerBaseIds,
  templateName?: string
): CreateTriggerRequest {
  return {
    baseid: baseIds.baseid,
    environmentId: baseIds.environmentId,
    isfile: false,
    isrecurring: true,
    iswebhook: false,
    templateName: templateName || undefined,
  };
}

function buildCronFields(
  mode: 'create' | 'edit',
  cron: CronScheduleSaveState,
  cronBuilderRef: RefObject<CronScheduleBuilderHandle | null>,
  storedCronExplain?: string,
  confirmLegacyMigration?: boolean,
  cronTimezoneBootstrap?: boolean,
  openedForCronMigration?: boolean,
  storedCronTimeZone?: string,
  storedCronTimeFrame?: CreateTriggerRequest['cronTimeFrame']
): Pick<CreateTriggerRequest, 'cronexpression' | 'cronexplain' | 'cronTimeFrame'> {
  const built = cronBuilderRef.current?.buildForSave();

  if (mode === 'create') {
    if (!built?.isValid) {
      throw new Error('Please fix the cron schedule before saving');
    }
    return {
      cronexpression: built.expression,
      cronexplain: built.explain,
      cronTimeFrame: built.cronTimeFrame ?? null,
    };
  }

  const metadataOnlySave = (openedForCronMigration || cronTimezoneBootstrap) && !built?.userEdited;

  if (metadataOnlySave && !confirmLegacyMigration) {
    return {
      cronexpression: cron.canonicalExpression ?? cron.expression,
      cronexplain: storedCronExplain ?? cron.explain,
      cronTimeFrame: built?.cronTimeFrame ?? undefined,
    };
  }

  const storedTimeZone = storedCronTimeZone?.trim();
  const timeZoneChanged = Boolean(storedTimeZone) && cron.timeZone.trim() !== storedTimeZone;

  const shouldRebuildFromBuilder =
    Boolean(built?.userEdited) || Boolean(cron.isDirty) || timeZoneChanged;

  if (shouldRebuildFromBuilder) {
    if (!built?.isValid) {
      throw new Error('Please fix the cron schedule before saving');
    }
    return {
      cronexpression: built.expression,
      cronexplain: built.explain,
      cronTimeFrame: built.cronTimeFrame ?? null,
    };
  }

  const { cronexpression, cronexplain } = resolveEditCronSavePayload(
    {
      ...cron,
      isDirty: false,
      expression: cron.canonicalExpression ?? cron.expression,
    },
    storedCronExplain,
    { confirmLegacyMigration }
  );

  return {
    cronexpression,
    cronexplain,
    cronTimeFrame: built?.cronTimeFrame ?? storedCronTimeFrame ?? null,
  };
}

/**
 * Unified create/edit trigger payload builder for schedule dialogs.
 * Lifts logic from CreateScheduleDialog, EditScheduleDialog, and recipe editor.
 */
export function buildScheduleTriggerRequest(
  input: BuildScheduleTriggerRequestInput
): CreateTriggerRequest {
  const {
    mode,
    scheduleType,
    daily,
    repeating,
    cron,
    cronBuilderRef,
    baseIds,
    templateName,
    refDate = new Date(),
    storedCronExplain,
    repeatingLegacyNoTimeZone,
    confirmLegacyMigration,
  } = input;

  const baseRequest = buildBaseRequest(baseIds, templateName);

  switch (scheduleType) {
    case 'daily': {
      const utc = convertLocalTimeToUTC(daily.hour, daily.minute, daily.timeZone, refDate);
      return {
        ...baseRequest,
        dailySchedule: {
          timeUTC: `${utc.hour.toString().padStart(2, '0')}:${utc.minute.toString().padStart(2, '0')}`,
          timeZone: daily.timeZone,
        },
      };
    }

    case 'repeating':
      return {
        ...baseRequest,
        repeatingSchedule: buildRepeatingSchedulePayload(repeating, refDate, {
          legacyBrowserLocal:
            mode === 'edit' && repeatingLegacyNoTimeZone && !repeating.timeZone.trim(),
        }),
      };

    case 'cron': {
      const cronFields = buildCronFields(
        mode,
        cron,
        cronBuilderRef,
        storedCronExplain,
        confirmLegacyMigration,
        input.cronTimezoneBootstrap,
        input.openedForCronMigration,
        input.storedCronTimeZone,
        input.storedCronTimeFrame
      );
      return {
        ...baseRequest,
        ...cronFields,
        cronTimeZone: cron.timeZone.trim(),
      };
    }

    default:
      throw new Error('Invalid schedule type');
  }
}
